import React, { useState } from 'react';
import { MessageSquare, Send, Search, Loader2, User } from 'lucide-react';
import { useConversations, useMessages, useSendMessage, useMarkMessageAsRead } from '../../services/messageService';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/shared/Button';
import Input from '../../components/shared/Input';

const VendorMessages = () => {
  const { user } = useAuth();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const { data: conversationsData, isLoading: conversationsLoading } = useConversations();
  // Ensure conversations is always an array
  const conversations = Array.isArray(conversationsData?.data) 
    ? conversationsData.data 
    : Array.isArray(conversationsData) 
    ? conversationsData 
    : [];

  const { data: messagesData, isLoading: messagesLoading } = useMessages(selectedConversation?.id);
  // Ensure messages is always an array
  const messages = Array.isArray(messagesData?.data) 
    ? messagesData.data 
    : Array.isArray(messagesData) 
    ? messagesData 
    : [];

  const sendMessageMutation = useSendMessage({
    onSuccess: () => {
      setMessageText('');
    },
  });

  const markAsReadMutation = useMarkMessageAsRead();

  const filteredConversations = Array.isArray(conversations) ? conversations.filter(conv => {
    const otherUser = conv.participants?.find(p => p.id !== user?.id);
    const searchLower = searchTerm.toLowerCase();
    return !searchTerm || 
           conv.last_message?.toLowerCase().includes(searchLower) ||
           otherUser?.name?.toLowerCase().includes(searchLower);
  }) : [];

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    // Mark messages as read when opening conversation
    if (conversation.unread_count > 0) {
      // Mark all unread messages as read
      messages.forEach(msg => {
        if (!msg.is_read && msg.sender_id !== user?.id) {
          markAsReadMutation.mutate(msg.id);
        }
      });
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!messageText.trim() || !selectedConversation) return;

    sendMessageMutation.mutate({
      conversationId: selectedConversation.id,
      message: messageText.trim(),
    });
  };

  if (conversationsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Messages</h1>
          <p className="text-gray-600 mt-1">Communicate with your clients</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex flex-col lg:flex-row h-[calc(100vh-12rem)]">
            {/* Conversations List */}
            <div className="w-full lg:w-1/3 border-r border-gray-200 flex flex-col">
              {/* Search */}
              <div className="p-4 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    type="text"
                    placeholder="Search conversations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Conversations */}
              <div className="flex-1 overflow-y-auto">
                {filteredConversations.length === 0 ? (
                  <div className="p-8 text-center">
                    <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No conversations found</p>
                  </div>
                ) : (
                  filteredConversations.map((conversation) => {
                    const otherUser = conversation.participants?.find(p => p.id !== user?.id);
                    const isSelected = selectedConversation?.id === conversation.id;
                    
                    return (
                      <div
                        key={conversation.id}
                        onClick={() => handleSelectConversation(conversation)}
                        className={`p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors ${
                          isSelected ? 'bg-primary-50 border-l-4 border-l-primary-500' : ''
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                            {otherUser?.profile_image_url ? (
                              <img
                                src={otherUser.profile_image_url}
                                alt={otherUser.name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <User className="w-5 h-5 text-primary-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="text-sm font-semibold text-gray-900 truncate">
                                {otherUser?.name || 'Unknown User'}
                              </h3>
                              {conversation.unread_count > 0 && (
                                <span className="flex-shrink-0 ml-2 px-2 py-0.5 text-xs font-medium bg-primary-600 text-white rounded-full">
                                  {conversation.unread_count}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 truncate">
                              {conversation.last_message || 'No messages yet'}
                            </p>
                            {conversation.last_message_time && (
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(conversation.last_message_time).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 flex flex-col">
              {selectedConversation ? (
                <>
                  {/* Messages Header */}
                  <div className="p-4 border-b border-gray-200">
                    {(() => {
                      const otherUser = selectedConversation.participants?.find(p => p.id !== user?.id);
                      return (
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                            {otherUser?.profile_image_url ? (
                              <img
                                src={otherUser.profile_image_url}
                                alt={otherUser.name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <User className="w-5 h-5 text-primary-600" />
                            )}
                          </div>
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900">
                              {otherUser?.name || 'Unknown User'}
                            </h3>
                            <p className="text-xs text-gray-500">
                              {otherUser?.email || ''}
                            </p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Messages List */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messagesLoading ? (
                      <div className="flex justify-center items-center h-full">
                        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="flex justify-center items-center h-full">
                        <p className="text-gray-500">No messages yet. Start the conversation!</p>
                      </div>
                    ) : (
                      messages.map((message) => {
                        const isOwnMessage = message.sender_id === user?.id;
                        return (
                          <div
                            key={message.id}
                            className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                                isOwnMessage
                                  ? 'bg-primary-600 text-white'
                                  : 'bg-gray-100 text-gray-900'
                              }`}
                            >
                              <p className="text-sm">{message.message}</p>
                              <p
                                className={`text-xs mt-1 ${
                                  isOwnMessage ? 'text-primary-100' : 'text-gray-500'
                                }`}
                              >
                                {new Date(message.created_at).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Message Input */}
                  <div className="p-4 border-t border-gray-200">
                    <form onSubmit={handleSendMessage} className="flex gap-2">
                      <Input
                        type="text"
                        placeholder="Type a message..."
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        type="submit"
                        icon={Send}
                        disabled={!messageText.trim() || sendMessageMutation.isPending}
                        loading={sendMessageMutation.isPending}
                      >
                        Send
                      </Button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Select a conversation to start messaging</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorMessages;

